import React, { useState, useRef, useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { FiLayers, FiCheckCircle, FiTrendingUp, FiAlertTriangle } from "react-icons/fi";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import StatusChip from "../../../Common/StatusChip";
import DateRangeFilter from "../../../../Common/DateFilter";
import ReportCard from "../../../Common/ReportCard";
import { BOQ_CONSUMPTION_DATA } from "./storeReportsData";

const BOQConsumptionReconciliationReport = () => {
  const gridRef = useRef(null);
  const [data] = useState(BOQ_CONSUMPTION_DATA);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [columnVisibility, setColumnVisibility] = useState({});

  // Summary KPI metrics
  const kpis = useMemo(() => {
    const totalTasks = data.length;
    const withinAllowanceCount = data.filter((r) => r.status === "Within Allowance").length;
    const nearLimitCount = data.filter((r) => r.status === "Near Limit >90%").length;
    const overDrawnCount = data.filter((r) => r.status === "Over-Drawn Alert").length;

    return {
      totalTasks,
      withinAllowanceCount,
      nearLimitCount,
      overDrawnCount,
    };
  }, [data]);

  // Column definitions
  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "BOQ Reference & Work Item",
        field: "boqRef",
        colId: "boqRef",
        flex: 2,
        minWidth: 260,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 600, color: "var(--color-primary, #0b6462)" }}>{p.value}</span>
        ),
      },
      {
        headerName: "Assigned Subcontractor / Thekedar",
        field: "subcontractor",
        colId: "subcontractor",
        flex: 1.5,
        minWidth: 190,
        cellRenderer: (p) => <span style={{ fontWeight: 600, color: "#1e293b" }}>{p.value}</span>,
      },
      {
        headerName: "Material SKU / Description",
        field: "materialSku",
        colId: "materialSku",
        flex: 1.8,
        minWidth: 210,
      },
      {
        headerName: "BOQ Planned Limit (Qty + Wastage)",
        field: "plannedLimit",
        colId: "plannedLimit",
        flex: 1.3,
        minWidth: 160,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 600, color: "#334155" }}>
            {p.value !== undefined ? Number(p.value).toLocaleString() : "0"}{" "}
            <span style={{ fontSize: "11px", color: "#64748b" }}>{p.data?.uom}</span>
          </span>
        ),
      },
      {
        headerName: "Cumulative Issued Qty to Date",
        field: "cumulativeIssued",
        colId: "cumulativeIssued",
        flex: 1.3,
        minWidth: 160,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 700, color: "#0f172a" }}>
            {p.value !== undefined ? Number(p.value).toLocaleString() : "0"}{" "}
            <span style={{ fontSize: "11px", color: "#64748b" }}>{p.data?.uom}</span>
          </span>
        ),
      },
      {
        headerName: "Variance / Balance Remaining",
        field: "varianceRemaining",
        colId: "varianceRemaining",
        flex: 1.4,
        minWidth: 170,
        cellRenderer: (p) => {
          const val = p.value || 0;
          const isNegative = val < 0;
          return (
            <span
              style={{
                fontWeight: 700,
                color: isNegative ? "#dc2626" : "#16a34a",
              }}
            >
              {isNegative ? "" : "+"}
              {Number(val).toLocaleString()} <span style={{ fontSize: "11px" }}>{p.data?.uom}</span>
            </span>
          );
        },
      },
      {
        headerName: "Consumption Status",
        field: "status",
        colId: "status",
        flex: 1.3,
        minWidth: 160,
        cellRenderer: (p) => {
          const val = p.value || "Within Allowance";
          let type = "success";
          if (val === "Over-Drawn Alert") {
            type = "danger";
          } else if (val === "Near Limit >90%") {
            type = "warning";
          }
          return <StatusChip status={val} type={type} />;
        },
      },
    ];
  }, []);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
    }),
    [],
  );

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const q = searchTerm.trim().toLowerCase();
      const matchesSearch =
        !q ||
        (item.boqRef && item.boqRef.toLowerCase().includes(q)) ||
        (item.subcontractor && item.subcontractor.toLowerCase().includes(q)) ||
        (item.materialSku && item.materialSku.toLowerCase().includes(q));

      const matchesStatus =
        statusFilter === "all" || item.status?.toLowerCase() === statusFilter.toLowerCase();

      return matchesSearch && matchesStatus;
    });
  }, [data, searchTerm, statusFilter]);

  const onGridReady = useCallback((params) => {
    const map = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        map[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(map);
  }, []);

  const toggleColumn = useCallback(
    (colId) => {
      if (!gridRef.current?.api) {
        return;
      }
      const isCurrentlyVisible = columnVisibility[colId];
      gridRef.current.api.setColumnsVisible([colId], !isCurrentlyVisible);
      setColumnVisibility((prev) => ({ ...prev, [colId]: !isCurrentlyVisible }));
    },
    [columnVisibility],
  );

  const handleExportCSV = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: `BOQ_Material_Consumption_Reconciliation_${new Date()
          .toISOString()
          .slice(0, 10)}.csv`,
      });
      toast.success("BOQ reconciliation report exported to CSV");
    }
  };

  return (
    <div className="pt-2 app_font">
      {/* ── Summary Cards ── */}
      <div className="row g-2 mb-1 mt-1">
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="TOTAL TASKS MONITORED"
            value={`${kpis.totalTasks} Packages`}
            subtitle="Active BOQ work packages"
            icon={FiLayers}
            color="#0b6462"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="WITHIN ALLOWED LIMIT"
            value={`${kpis.withinAllowanceCount} Packages`}
            subtitle="Compliant with BOQ limits"
            icon={FiCheckCircle}
            color="#16a34a"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="NEAR LIMIT WARNING (>90%)"
            value={`${kpis.nearLimitCount} Packages`}
            subtitle="Approaching consumption limit"
            icon={FiTrendingUp}
            color="#ea580c"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="OVER-DRAWN ALERTS"
            value={`${kpis.overDrawnCount} Over-Draws`}
            subtitle="Immediate audit required"
            icon={FiAlertTriangle}
            color="#dc2626"
          />
        </div>
      </div>

      {/* ── Filter Toolbar (General Journal style) ── */}
      <div className="row app_font">
        <div className="col-12 pt-1 p-0">
          <div
            className="d-flex flex-wrap align-items-center justify-content-end pt-2 pb-2 app_font w-100"
            style={{ gap: "12px", width: "100%" }}
          >
            <div
              className="d-flex align-items-center"
              style={{
                minHeight: "30px",
                maxWidth: "100%",
                marginRight: "auto",
                padding: "4px 12px",
                color: "var(--btn-comp-text, #0b6462)",
                fontSize: "var(--font-md)",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              title="BOQ Consumption vs Limit"
            >
              BOQ Consumption vs Limit
            </div>
            <SearchFilter
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search BOQ, subcontractor, SKU..."
            />

            <div className="d-flex align-items-center" style={{ maxWidth: "260px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <DateRangeFilter
                  initialFrom={dateRange.from}
                  initialTo={dateRange.to}
                  onChange={(val) => setDateRange(val)}
                />
                <label>Date</label>
              </div>
            </div>

            <div className="d-flex align-items-center" style={{ maxWidth: "240px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Consumption Statuses</option>
                  <option value="within allowance">Within Allowance</option>
                  <option value="near limit >90%">Near Limit &gt;90%</option>
                  <option value="over-drawn alert">Over-Drawn Alert</option>
                </select>
                <label>Consumption Status</label>
              </div>
            </div>

            <ColumnVisibilityControl
              gridApi={gridRef.current?.api}
              columnVisibility={columnVisibility}
              toggleColumn={toggleColumn}
            />
          </div>
        </div>
      </div>

      {/* ── AG Grid Table ── */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div
            className="ag-theme-alpine vouchers_table_height3 mt-1"
            style={{ width: "100%", position: "relative" }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={15}
              tooltipShowDelay={0}
              onGridReady={onGridReady}
              rowStyle={{ cursor: "pointer" }}
            />
            {/* Table Footer Actions */}
            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "15px",
                display: "flex",
                gap: "10px",
                zIndex: 1,
              }}
            >
              <ActionButton
                text="Download CSV"
                isPrint={true}
                newBtn="true"
                onClick={handleExportCSV}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BOQConsumptionReconciliationReport;
